<template>
  <div class="doctor-advice-statistics">
    <div class="statistics-card">
      <!-- 标题栏 -->
      <div class="card-header">
        <div class="header-title">
          <i class="el-icon-document"></i>
          <span>处方毛利表</span>
        </div>
      </div>

      <!-- 查询条件 -->
      <div class="query-form">
        <el-form :model="queryParams" size="small" label-width="80px" inline>
          <el-form-item label="科室">
            <el-select
                v-model="queryParams.orderingDeptId"
                placeholder="请选择科室"
                clearable
                filterable
                @change="handleSearch"
            >
              <el-option
                  v-for="item in deptOptions"
                  :key="item.id"
                  :label="item.label"
                  :value="item.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="医生">
            <el-select
                v-model="queryParams.orderingDoctorId"
                placeholder="请选择医生"
                clearable
                filterable
                @change="handleSearch"
            >
              <el-option
                  v-for="item in doctorOptions"
                  :key="item.id"
                  :label="item.label"
                  :value="item.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="统计时间">
            <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                @change="handleTimeChange"
                :picker-options="pickerOptions"
            />
          </el-form-item>

          <el-form-item>
            <el-button @click="handleReset" icon="el-icon-refresh-right">重置</el-button>
            <el-button type="success" @click="handleExport" icon="el-icon-download">导出</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 统计卡片区域 -->
      <div class="statistics-cards">
        <div class="stat-card">
          <div class="stat-card-icon total-icon">
            <i class="el-icon-s-data"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">合计金额</div>
            <div class="stat-value total-value">
              {{ formatAmount(summaryData.totalAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon cost-icon">
            <i class="el-icon-s-order"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">成本金额</div>
            <div class="stat-value cost-value">
              {{ formatAmount(summaryData.totalCostAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon profit-icon">
            <i class="el-icon-s-marketing"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">毛利金额</div>
            <div class="stat-value profit-value">
              {{ formatAmount(summaryData.totalAmount - summaryData.totalCostAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon rate-icon">
            <i class="el-icon-pie-chart"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">毛利率</div>
            <div class="stat-value rate-value">
              {{ formatRate(summaryData.totalAmount, summaryData.totalCostAmount) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="table-container">
        <el-table
            :data="tableData"
            border
            stripe
            v-loading="loading"
            style="width: 100%"
        >
          <el-table-column prop="categoryName" label="分类名称" min-width="120" align="center" />
          <el-table-column prop="amount" label="金额" min-width="120" align="right">
            <template #default="{ row }">
              {{ formatAmount(row.amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="costAmount" label="成本金额" min-width="120" align="right">
            <template #default="{ row }">
              {{ formatAmount(row.costAmount) }}
            </template>
          </el-table-column>
          <el-table-column label="毛利" min-width="120" align="right">
            <template #default="{ row }">
              {{ formatAmount(row.amount - row.costAmount) }}
            </template>
          </el-table-column>
          <el-table-column label="毛利率" min-width="120" align="center">
            <template #default="{ row }">
              {{ formatRate(row.amount, row.costAmount) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { doctorAdviceStatistics } from "@/api/reservation/outpatientCharge";

export default {
  name: "DoctorAdviceStatistics",
  data() {
    return {
      loading: false,
      // 时间范围（默认当月）
      timeRange: [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      ],
      // 查询参数（新接口字段名）
      queryParams: {
        orderingDeptId: null,
        orderingDoctorId: null,
        startTime: dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      },
      // 表格数据（分类统计）
      tableData: [],
      // 汇总数据
      summaryData: {
        totalAmount: 0,
        totalCostAmount: 0
      },
      // 科室选项
      deptOptions: [],
      // 医生选项
      doctorOptions: [],
      // 分类编码映射
      categoryMap: {
        1: '西药',
        2: '中成药',
        4: '理疗',
        5: '卫材',
        10: '其他'
      },
      // 时间选择器快捷选项
      pickerOptions: {
        shortcuts: [
          {
            text: '今天',
            onClick(picker) {
              const start = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '昨天',
            onClick(picker) {
              const start = dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本周',
            onClick(picker) {
              const start = dayjs().startOf('week').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('week').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本月',
            onClick(picker) {
              const start = dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '上月',
            onClick(picker) {
              const start = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本季度',
            onClick(picker) {
              const start = dayjs().startOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本年',
            onClick(picker) {
              const start = dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('year').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          }
        ]
      }
    };
  },
  mounted() {
    this.initOptions();
    this.loadData();
  },
  methods: {
    // 初始化下拉选项
    initOptions() {
      this.deptOptions = this.$store.getters['preload/getDeptListOptions'];
      this.doctorOptions = this.$store.getters['preload/getDoctorListOptions'];
    },

    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const params = {
          orderingDeptId: this.queryParams.orderingDeptId || null,
          orderingDoctorId: this.queryParams.orderingDoctorId || null,
          startTime: this.queryParams.startTime,
          endTime: this.queryParams.endTime
        };

        const res = await doctorAdviceStatistics(params);
        if (res.code === 200) {
          const data = res.data || {};
          // 处理分类数据，添加分类名称
          const categories = (data.categories || []).map(item => ({
            ...item,
            categoryName: this.categoryMap[item.categoryCode] || `分类${item.categoryCode}`
          }));
          this.tableData = categories;
          this.summaryData = {
            totalAmount: data.totalAmount || 0,
            totalCostAmount: data.totalCostAmount || 0
          };
        } else {
          this.$message.error(res.message || '获取数据失败');
        }
      } catch (error) {
        console.error('加载医嘱统计数据失败:', error);
        this.$message.error('系统异常，请联系管理员');
      } finally {
        this.loading = false;
      }
    },

    // 搜索（主动触发查询）
    handleSearch() {
      this.loadData();
    },

    // 重置
    handleReset() {
      this.queryParams = {
        orderingDeptId: null,
        orderingDoctorId: null,
        startTime: dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      };
      this.timeRange = [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      ];
      this.loadData();
    },

    // 时间变化
    handleTimeChange(val) {
      if (val && val.length === 2) {
        this.queryParams.startTime = val[0];
        this.queryParams.endTime = val[1];
        this.handleSearch();
      }
    },

    // 格式化金额
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = Number(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // 格式化毛利率
    formatRate(totalAmount, costAmount) {
      const total = Number(totalAmount) || 0;
      const cost = Number(costAmount) || 0;
      if (total === 0) return '0.00%';
      const rate = ((total - cost) / total) * 100;
      return rate.toFixed(2) + '%';
    },

    // 导出报表
    handleExport() {
      if (this.tableData.length === 0) {
        this.$message.warning('暂无数据可导出');
        return;
      }

      // 构建导出数据
      const exportData = this.tableData.map(row => ({
        '分类名称': row.categoryName || '',
        '金额': this.formatAmount(row.amount),
        '成本金额': this.formatAmount(row.costAmount),
        '毛利': this.formatAmount(row.amount - row.costAmount),
        '毛利率': this.formatRate(row.amount, row.costAmount)
      }));

      // 添加合计行
      const total = this.summaryData;
      exportData.push({
        '分类名称': '合计',
        '金额': this.formatAmount(total.totalAmount),
        '成本金额': this.formatAmount(total.totalCostAmount),
        '毛利': this.formatAmount(total.totalAmount - total.totalCostAmount),
        '毛利率': this.formatRate(total.totalAmount, total.totalCostAmount)
      });

      // 转换为 CSV
      const headers = Object.keys(exportData[0]);
      const csvRows = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ];
      const csvContent = csvRows.join('\n');

      // 下载文件
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `医嘱统计_${dayjs().format('YYYYMMDDHHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$message.success('导出成功');
    }
  }
};
</script>

<style scoped lang="less">
.doctor-advice-statistics {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;

  .statistics-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      padding: 16px 24px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;

      .header-title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;

        i {
          font-size: 24px;
          margin-right: 10px;
        }
      }
    }

    .query-form {
      padding: 16px 24px;
      border-bottom: 1px solid #ebeef5;
      background: #fafbfc;
    }

    .statistics-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      padding: 24px;

      .stat-card {
        display: flex;
        align-items: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-card-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          margin-right: 16px;
          font-size: 28px;

          &.total-icon {
            background: linear-gradient(135deg, #e6f7ff, #bae7ff);
            color: #409EFF;
          }

          &.cost-icon {
            background: linear-gradient(135deg, #fff7e6, #ffe7ba);
            color: #E6A23C;
          }

          &.profit-icon {
            background: linear-gradient(135deg, #f6ffed, #d9f7be);
            color: #67C23A;
          }

          &.rate-icon {
            background: linear-gradient(135deg, #f9f0ff, #efdbff);
            color: #722ED1;
          }
        }

        .stat-card-content {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 24px;
            font-weight: bold;

            .unit {
              font-size: 12px;
              font-weight: normal;
              margin-left: 4px;
              color: #909399;
            }

            &.total-value {
              color: #409EFF;
            }

            &.cost-value {
              color: #E6A23C;
            }

            &.profit-value {
              color: #67C23A;
            }

            &.rate-value {
              color: #722ED1;
            }
          }
        }
      }
    }

    .table-container {
      padding: 0 24px 24px 24px;
    }
  }
}

// 响应式布局
@media (max-width: 1200px) {
  .doctor-advice-statistics .statistics-card .statistics-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .doctor-advice-statistics {
    padding: 12px;

    .statistics-card {
      .card-header {
        padding: 12px 16px;
      }

      .query-form {
        padding: 12px 16px;

        :deep(.el-form-item) {
          display: block;
          margin-bottom: 12px;
        }
      }

      .statistics-cards {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 16px;
      }

      .table-container {
        padding: 0 16px 16px 16px;
        overflow-x: auto;
      }
    }
  }
}
</style>